import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0111 from "../lesson-11/lesson";
import lesson0114 from "../lesson-14/lesson";

const linkLesson0111 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0111.id);
const linkLesson0114 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0114.id);

const theory = `   <h3 class=sectionHead>Decomposition of a sound field based on spherical harmonics</h3>
<p>If any compact source can be decomposed as a combination of multipoles, any pressure field can be decomposed on the
   basis of spherical harmonics (that is to say, functions of which the Laplacian is null). In spherical coordinates, the
   Laplacian in spherical coordinates writes
</p>
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>Δ</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂r</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mfrac>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi>∂</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>∂ϕ</mi>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>ϕ</mi>
                  <mfrac>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂ϕ</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
                  <mi>ϕ</mi>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>∂</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi>∂</mi>
                  <msup>
                     <mrow>
                        <mi>𝜃</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
   </tr>
</table>
<p>The method of separation of variables seen for the cartesian coordinates in <a href="${linkLesson0111}" target=_blank>Lesson 1.11</a> may be followed to find
   solutions in spherical coordinates. As seen before this method gives access implicitly only to solutions in the
   frequency domain which writes
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jmϕ</mi>
               </mrow>
            </msup>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>B</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      </td>
      <td class=eq-no>(2)
      </td>
   </tr>
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   is a relative integer and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   a natural integer,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   is the Legendre associated polynomial of degree
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   with parameter
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>j</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   are the spherical Bessel functions of the first and second species respectively. The second is sometime called
   Neumann function. These functions are related to classical Bessel functions
   <math
      xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>J</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   by the relations
</p>
<table class=equation>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>j</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi mathvariant=italic>kr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <msub>
               <mrow>
                  <mi>J</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      </td>
      <td class=eq-no>(3)
      </td>
   </tr>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                        <mi mathvariant=italic>kr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <msub>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      </td>
      <td class=eq-no>(4)
   </tr>
</table>
<p>In the far field these functions reduce to</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
         <msub>
            <mrow>
               <mi>j</mi>
            </mrow>
            <mrow>
               <mi>n</mi>
            </mrow>
         </msub>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant=italic>kr</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>≈</mo>
         <mfrac>
            <mrow>
               <mi class=qopname>sin</mi>
               <mo>⁡</mo>
               <mo class=MathClass-open>(</mo>
               <mi mathvariant=italic>kr</mi>
               <mo class=MathClass-bin>+</mo>
               <mi>n</mi>
               <mfrac>
                  <mrow>
                     <mi>π</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </mfrac>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <mi mathvariant=italic>kr</mi>
            </mrow>
         </mfrac>
      </td>
      <td class=eq-no>(5)
   </tr>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <mi class=qopname>cos</mi>
                  <mo>⁡</mo>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-bin>+</mo>
                  <mi>n</mi>
                  <mfrac>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>kr</mi>
               </mrow>
            </mfrac>
         </math>
      </td>
      <td class=eq-no>(6)
   </tr>
</table>
<p>This last expression shows that Bessel functions are related to stationary waves which are not so common in the case
   of spherical waves. So, it is better to express the pressure by using Hankel functions
</p>
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jmϕ</mi>
               </mrow>
            </msup>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>h</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>m</mi>
                        <mo class=MathClass-punc>,</mo>
                        <mi>n</mi>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>h</mi>
                     </mrow>
                     <mrow>
                        <mi>n</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-open>(</mo>
                  <mi mathvariant=italic>kr</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      </td>
      <td class=eq-no>(7)
   </tr>
</table>
<p>Hankel Functions are defined as</p>
<table class=equation>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>j</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mi>j</mi>
            <mo class=MathClass-punc>.</mo>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      </td>
      <td class=eq-no>(8)</mtext>
   </tr>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML class=equation display=block>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>j</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi>j</mi>
            <mo class=MathClass-punc>.</mo>
            <msub>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      </td>
      <td class=eq-no>(9)
   </tr>
</table>
<p>So for the far field we have</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>j</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>−</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </mfrac>
         </math>
      </td>
      <td class=eq-no>(10)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>≈</mo>
            <msup>
               <mrow>
                  <mi>j</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msup>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-bin>+</mo>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </mfrac>
         </math>
      </td>
      <td class=eq-no>(11)
   </tr>
</table>
<p>
   First function corresponds to divergent progressive waves while the second corresponds to convergent waves. For first
   order there is an equivalence between spherical harmonics and multipoles (see <a href="${linkLesson0114}" target=_blank>Lesson 1.14</a>).
   <math
      xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
   </math>
   corresponds to the monopole.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>kr</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
   </math>
   corresponds to the dipole and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
      <mrow>
         <mo fence=true form=prefix>(</mo>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>3</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>3</mn>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mo class=MathClass-open>(</mo>
                        <mi mathvariant=italic>jkr</mi>
                        <mo class=MathClass-close>)</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
         </mrow>
         <mo fence=true form=postfix>)</mo>
      </mrow>
   </math>
   to quadrupole. Higher order are given by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>j</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msup>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <munderover accentunder=false accent=false>
         <mrow>
            <mi class=MathClass-op>∑</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mi>s</mi>
            <mo class=MathClass-rel>=</mo>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </munderover>
      <mfrac>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi>n</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>s</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc>!</mo>
         </mrow>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi>n</mi>
            <mo class=MathClass-bin>−</mo>
            <mi>s</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-punc>!</mo>
            <mi>s</mi>
            <mo class=MathClass-punc>!</mo>
         </mrow>
      </mfrac>
      <msup>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>jkr</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msup>
   </math>
   the Stokes function which is a polynomial in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
   </math>
   equal to unity in the far field. Hankel functions give the dependence of pressure with distance while
   spherical harmonics
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jmϕ</mi>
         </mrow>
      </msup>
   </math>
   give the directivity. Zero order normalised spherical harmonics is given by
</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      </td>
      <td class=eq-no>(12)
   </tr>
</table>
<p>This one correspond to monopolar component of the source. First orders are given by</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </math>
      </td>
      <td class=eq-no>(13)
   </tr>
   <tr>
      <td class=align-odd columnalign=right>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <mn>8</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(14)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                     <mrow>
                        <mn>8</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(15)
   </tr>
</table>
<p>
   These solutions can be related to dipoles:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msubsup>
   </math>
   dipole along
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>-axis,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msubsup>
   </math>
   dipole along
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>-axis and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>−</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msubsup>
   </math>
   dipole along
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
   </math>-axis. Second orders are given by
</p>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
         <msubsup>
            <mrow>
               <mi>Y</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
            <mrow>
               <mn>0</mn>
            </mrow>
         </msubsup>
         <mo class=MathClass-rel>=</mo>
         <msqrt>
            <mrow>
               <mfrac>
                  <mrow>
                     <mn>5</mn>
                  </mrow>
                  <mrow>
                     <mn>16</mn>
                     <mi>π</mi>
                  </mrow>
               </mfrac>
            </mrow>
         </msqrt>
         <mo class=MathClass-open>(</mo>
         <mn>3</mn>
         <msup>
            <mrow>
               <mi class=qopname>cos</mi>
               <mo>⁡</mo>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <mi>𝜃</mi>
         <mo class=MathClass-bin>−</mo>
         <mn>1</mn>
         <mo class=MathClass-close>)</mo>
         </math
      </td>
      <td class=eq-no>(16)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>15</mn>
                     </mrow>
                     <mrow>
                        <mn>8</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(17)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>15</mn>
                     </mrow>
                     <mrow>
                        <mn>8</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(18)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>15</mn>
                     </mrow>
                     <mrow>
                        <mn>32</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <msup>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(19)
   </tr>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>15</mn>
                     </mrow>
                     <mrow>
                        <mn>32</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <msup>
               <mrow>
                  <mi class=qopname>sin</mi>
                  <mo>⁡</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mi>𝜃</mi>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mn>2</mn>
                  <mi mathvariant=italic>jϕ</mi>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(20)
   </tr>
</table>
<p>
   These solution are related to quadrupoles:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msubsup>
   </math>
   corresponds to a transverse quadrupole in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>xoz</mi>
   </math>
   plane while
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>−</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msubsup>
   </math>
   corresponds to a transverse quadrupole in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>yoz</mi>
   </math>
   plane.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msubsup>
   </math>
   correspond to transverse quadrupoles in
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>xoy</mi>
   </math>
   plane shifted by 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>45</mn>
   </math><span class=tcrm-1000>°</span>. Finally, 
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msubsup>
   </math>
   corresponds two a longitudinal quadrupole along
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi></math>-axis partly compensated with a monopole. Indeed, pressure of the longitudinal quadupole can be written (see
   <a href="${linkLesson0114}" target=_blank>Lesson 1.14</a>)
</p>
<table class=equation>
<tr>
   <td style="font-size: 9px;">
      <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
         <mi>p</mi>
         <mo class=MathClass-open>(</mo>
         <mi mathvariant="bold">r</mi>
         <mo class=MathClass-punc>,</mo>
         <mi>ω</mi>
         <mo class=MathClass-close>)</mo>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>p</mi>
                  </mrow>
                  <mrow>
                     <mi>∞</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-open>(</mo>
               <mi>r</mi>
               <mo class=MathClass-punc>,</mo>
               <mi>ω</mi>
               <mo class=MathClass-close>)</mo>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </mfrac>
         <mrow>
            <mo fence=true form=prefix>(</mo>
            <mrow>
               <mo class=MathClass-open>(</mo>
               <mn>3</mn>
               <msup>
                  <mrow>
                     <mi class=qopname>cos</mi>
                     <mo>⁡</mo>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
               <mi>𝜃</mi>
               <mo class=MathClass-bin>−</mo>
               <mn>1</mn>
               <mo class=MathClass-close>)</mo>
               <mrow>
                  <mo fence=true form=prefix>(</mo>
                  <mrow>
                     <mn>1</mn>
                     <mo class=MathClass-bin>+</mo>
                     <mfrac>
                        <mrow>
                           <mn>3</mn>
                        </mrow>
                        <mrow>
                           <mi mathvariant=italic>jkr</mi>
                        </mrow>
                     </mfrac>
                     <mo class=MathClass-bin>+</mo>
                     <mfrac>
                        <mrow>
                           <mn>3</mn>
                        </mrow>
                        <mrow>
                           <msup>
                              <mrow>
                                 <mo class=MathClass-open>(</mo>
                                 <mi mathvariant=italic>jkr</mi>
                                 <mo class=MathClass-close>)</mo>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                              </mrow>
                           </msup>
                        </mrow>
                     </mfrac>
                  </mrow>
                  <mo fence=true form=postfix>)</mo>
               </mrow>
               <mo class=MathClass-bin>+</mo>
               <mn>1</mn>
            </mrow>
            <mo fence=true form=postfix>)</mo>
         </mrow>
      </math>
   </td>
   <td class=eq-no>(21)
</tr>
</table
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mi>∞</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>r</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   far field pressure for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>, while pressure related to spherical harmonic
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msubsup>
   </math>
   writes
</p>
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant="bold">r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>p</mi>
                     </mrow>
                     <mrow>
                        <mi>∞</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-punc>,</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>3</mn>
               </mrow>
            </mfrac>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mn>3</mn>
                  <msup>
                     <mrow>
                        <mi class=qopname>cos</mi>
                        <mo>⁡</mo>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>𝜃</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mn>1</mn>
                  <mo class=MathClass-close>)</mo>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>jkr</mi>
                           </mrow>
                        </mfrac>
                        <mo class=MathClass-bin>+</mo>
                        <mfrac>
                           <mrow>
                              <mn>3</mn>
                           </mrow>
                           <mrow>
                              <msup>
                                 <mrow>
                                    <mo class=MathClass-open>(</mo>
                                    <mi mathvariant=italic>jkr</mi>
                                    <mo class=MathClass-close>)</mo>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msup>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </math>
      </td>
      <td class=eq-no>(22)
   </tr>
</table>
<p>So a link can be made between multipoles decomposition and spherical harmonics but this is not straightforward for
   higher orders.
<h3 class=sectionHead>application to oscillating sphere</h3>
<p>
   We consider a sphere of radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   which oscillates sinusoidally along the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>-axis with a speed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
   </math> (see <a href="#" data-img-src=${config.ASSETS_URL}/01/15/theory/figure-1.svg data-img-alt="Figure 1. Oscillating sphere.">Figure 1</a>).
   <figure class=figure>
<p><img alt="Figure 1. Oscillating sphere." src=${config.ASSETS_URL}/01/15/theory/figure-1.svg>
<figcaption class=caption>Figure 1. Oscillating sphere.</figcaption>
</figure>
<p>
   The normal speed at any point of the sphere is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi mathvariant="bold">V</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-bin>⋅</mo>
      <mi mathvariant="bold">r</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi class=qopname>cos</mi>
      <mi>𝜃</mi>
   </math>. The problem has an axial symmetry and is thus independent of
   <math
      xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>. Pressure can be decomposed on the basis of spherical harmonics but as the Legendre polynomials constitute a
   basis and since
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <msub>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-close>)</mo>
   </math>, a single spherical harmonic is sufficient to represent the pressure field. So we simply have
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi>C</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open>(</mo>
            <mi mathvariant=italic>kr</mi>
            <mo class=MathClass-close>)</mo>
         </math>
      </td>
      <td class=eq-no>(23)
   </tr>
</table>
where
<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
   <mi>C</mi>
</math>
is determined by applying the continuity of the normal speed on the sphere. We have
<table class=equation>
   <tr>
      <td style="font-size: 9px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>v</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-rel>=</mo>
            <mi>R</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msub>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi mathvariant=italic>∂p</mi>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>∂r</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mi>C</mi>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <msub>
               <mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <mi>∂</mi>
                              <msubsup>
                                 <mrow>
                                    <mi>h</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>1</mn>
                                 </mrow>
                                 <mrow>
                                    <mo class=MathClass-bin>+</mo>
                                 </mrow>
                              </msubsup>
                           </mrow>
                           <mrow>
                              <mi mathvariant=italic>∂r</mi>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
         </math>
      </td>
      <td class=eq-no>(24)
   </tr>
</table>
<p>
   Knowing that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>+</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi mathvariant=italic>kr</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-bin>−</mo>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mi mathvariant=italic>kr</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>+</mo>
      <mfrac>
         <mrow>
            <mn>1</mn>
         </mrow>
         <mrow>
            <mi mathvariant=italic>jkr</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-close>)</mo>
   </math>
   we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mrow>
               <mo fence=true form=prefix>(</mo>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>∂</mi>
                        <msubsup>
                           <mrow>
                              <mi>h</mi>
                           </mrow>
                           <mrow>
                              <mn>1</mn>
                           </mrow>
                           <mrow>
                              <mo class=MathClass-bin>+</mo>
                           </mrow>
                        </msubsup>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>∂r</mi>
                     </mrow>
                  </mfrac>
               </mrow>
               <mo fence=true form=postfix>)</mo>
            </mrow>
         </mrow>
         <mrow>
            <mi>R</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mfrac>
         <mrow>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jkr</mi>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mo class=MathClass-bin>+</mo>
      <mn>2</mn>
      <mi mathvariant=italic>jkR</mi>
      <mo class=MathClass-bin>−</mo>
      <msup>
         <mrow>
            <mi>k</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <msup>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mo class=MathClass-close>)</mo>
   </math>, then
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>C</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>ω</mi>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mo class=MathClass-bin>+</mo>
            <mn>2</mn>
            <mi mathvariant=italic>jkR</mi>
            <mo class=MathClass-bin>−</mo>
            <msup>
               <mrow>
                  <mi>k</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msup>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
      </mfrac>
   </math>. Finally, we get
</p>
<table class=equation>
   <tr>
      <td style="font-size: 10px;">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>p</mi>
            <mo class=MathClass-open>(</mo>
            <mi>r</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-punc>,</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
            <msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mfrac>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>jkR</mi>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi mathvariant=italic>jkr</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mn>2</mn>
                  <mi mathvariant=italic>jkR</mi>
                  <mo class=MathClass-bin>−</mo>
                  <msup>
                     <mrow>
                        <mi>k</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <msup>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi mathvariant=italic>jk</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>r</mi>
                  <mo class=MathClass-bin>−</mo>
                  <mi>R</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </msup>
         </math>
      </td>
      <td class=eq-no>(25)
   </tr>
</table>
<p>We conclude that the oscillating sphere radiates like a dipole without needing to make an approximation.
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-Abramowitz class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>M. Abramowitz and I.A. Stegun. <span class=cmti-10>Handbook of mathematical functions</span>. Dover, 1965.
   <dt id=X0-Beranek class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>L.J. Beranek. <span class=cmti-10>Acoustics</span>. New York: The American Institute of Physics, 1986.
   <dt id=X0-Bruneau_98 class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>M. Bruneau. <span class=cmti-10>Manuel d’Acoustique Fondamentale</span>. Paris: Hermes, 1998.
   <dt id=X0-ChaigneKergomardEnglish class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>A. Chaigne and J. Kergomard. <span class=cmti-10>Acoustics of Musical Instruments</span>. New York: Springer,
         2016.
   <dt id=X0-Pierce class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>A.D. Pierce. <span class=cmti-10>Acoustics: an Introduction to its Physical Principles and </span><span
         class=cmti-10>Applications</span>. New York: MCGraw-Hill Book Company, Inc., 1981.
   <dt id=X0-Skudrzyk class=thebibliography>[6]
   <dd id=bib-6 class=thebibliography>
      <p>E. Skudrzyk. <span class=cmti-10>The Foundations of Acoustics—Basic Mathematics and </span><span
         class=cmti-10>Basic Acoustics</span>. Springer–Verlag, 1971.
   <dt id=X0-Temkin class=thebibliography>[7]
   <dd id=bib-7 class=thebibliography>
      <p>S. Temkin. <span class=cmti-10>Elements of Acoustics</span>. New York: Wiley, 1981.
</dl>`;

export default theory;
